import { HoverCharButton } from "@c/shared";
import LangDropdown from "@c/shared/LangDropdown";
import ThemeToggleButton from "@c/shared/ThemeToggleButton";
import { useAuth } from "@h/useCtx";
import { buildRoute } from "@u/routes";
import Button from "@ui/button";
import Logo from "@ui/logo";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, LayoutDashboard, LogIn } from "lucide-react";
import { conceptIcon } from "@u/conceptIcons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

type NavContext = "home" | "360";

type NavItem =
  | { type: "route"; key: string; label: string; to: string; end?: boolean }
  | { type: "anchor"; key: string; label: string; page: string; id: string };

/** Derive the active landing context from the path (lang prefix stripped).
 *  Landing única: el index ES la landing .360, así que el segmento vacío (y el
 *  legacy `/360`, que redirige acá) son contexto "360". El resto de las páginas
 *  de marketing (plans/portfolio/faq/book/legal) son "home". */
function getContext(pathname: string): NavContext {
  const seg = pathname.split("/").filter(Boolean)[1] ?? "";
  return seg === "" || seg === "360" ? "360" : "home";
}

export function HomeNavbar() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const context = getContext(location.pathname);


  // Context-aware center links. Anchors scroll in-page (or navigate page#id).
  const items = useMemo<NavItem[]>(() => {
    // "Inicio" intentionally omitted — the logo already links to the home page.
    const faqRoute: NavItem = {
      type: "route",
      key: "faq",
      label: "FAQ",
      to: buildRoute.faq(lang),
    };
    if (context === "360") {
      const page = buildRoute.threeSixty(lang);
      return [
        {
          type: "anchor",
          key: "services",
          label: t("pricing_eyebrow"),
          page,
          id: "pricing",
        },
        {
          type: "route",
          key: "pf",
          label: t("portfolio"),
          to: buildRoute.threeSixtyPortfolio(lang),
        },
        { type: "anchor", key: "faq", label: "FAQ", page, id: "faq" },
      ];
    }

    // home / generic marketing (plans/portfolio/faq/book/legal)
    return [
      // La landing no publica precios (/plans retirada 2026-09-16).
      {
        type: "route",
        key: "pf",
        label: t("portfolio"),
        to: buildRoute.portfolio(lang),
      },
      faqRoute,
    ];
  }, [context, t, lang]);

  // Close the mobile menu on route change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock scroll + Esc-to-close while the mobile overlay is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  function goAnchor(page: string, id: string) {
    setMenuOpen(false);
    if (location.pathname === page) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`${page}#${id}`);
    }
  }

  const ctaTo = user ? buildRoute.tours() : buildRoute.login();
  const ctaLabel = user ? t("dashboard") : t("login");

  // Contextual primary (filled) CTA: la venta es conversacional → demo. Sin
  // alta pública (2026-09-16); con sesión fuera de la landing .360 → dashboard.
  // El slot outline deja login/dashboard siempre a mano.
  const isThreeSixty = context === "360";
  const primaryTo =
    isThreeSixty || !user ? buildRoute.bookDemo(lang) : buildRoute.tours();
  const primaryLabel =
    isThreeSixty || !user ? t("demo_book_cta") : t("dashboard");
  const primaryIcon = isThreeSixty
    ? conceptIcon.bookDemo
    : user
      ? LayoutDashboard
      : ArrowRight;
  // Logged in outside .360 the primary CTA already is the dashboard — the
  // outline slot would render a second identical button.
  const showAccountCta = !user || isThreeSixty;

  return (
    <>
      {/* labelled: la home puede montar otros <nav>,
          so two unlabelled nav landmarks collide (axe landmark-unique) */}
      <nav
        aria-label={t("nav_main", "Principal")}
        className="sticky top-0 z-50 border-b border-border/20 bg-background/80 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 h-16 px-4 sm:px-6 lg:px-8">
          {/* Branding — Logo renders its own home link (asLink); no outer <a>.
              Wrapped so the auto-theme pair (color+white imgs) is a single
              flex child — otherwise justify-between pushes the dark logo off-left. */}
          <div className="flex shrink-0 items-center">
            <Logo variant="horizontal" theme="auto" size="xs" />
          </div>

          {/* Desktop Navigation — centered links fill the space between the
              logo and the right-hand controls. */}
          <div className="hidden xl:flex flex-1 items-center justify-center gap-5 2xl:gap-6">
            {items.map((item) => {
              if (item.type === "anchor") {
                return (
                  <button
                    key={item.key}
                    onClick={() => goAnchor(item.page, item.id)}
                    className="text-xs tracking-[0.12em] uppercase text-foreground hover:text-primary transition-colors duration-300 flex flex-col group/link relative"
                  >
                    {item.label}
                    <span className="h-px bg-primary w-0 group-hover/link:w-full transition-[width] duration-300 absolute -bottom-0.5" />
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  end={item.end}
                  className="text-xs tracking-[0.12em] uppercase text-foreground hover:text-primary transition-colors duration-300 flex flex-col group/link relative"
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`h-px bg-primary transition-[width] duration-300 absolute -bottom-0.5 ${
                          isActive ? "w-full" : "w-0 group-hover/link:w-full"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>

          {/* Right controls — pinned to the right edge */}
          <div className="hidden xl:flex items-center gap-3 shrink-0">
            <div className="h-4 w-px bg-border/40" />

            {/* Secondary controls — smaller squares (less important) */}
            <div className="flex items-center gap-1">
              <LangDropdown className="h-9 w-9" />
              <ThemeToggleButton className="h-9 w-9" />
            </div>

            {/* Primary actions — outline = account access, filled = the
                contextual conversion CTA (demo on .360, free signup elsewhere) */}
            <div className="flex items-center gap-2">
              {showAccountCta && (
                <Button
                  size="sm"
                  design="outline"
                  to={ctaTo}
                  label={ctaLabel}
                  icon={user ? LayoutDashboard : LogIn}
                  responsiveLabel="2xl"
                  floatingLabel={ctaLabel}
                  floatingPosition="bottom"
                  className="h-11"
                />
              )}

              <Button
                size="sm"
                to={primaryTo}
                label={primaryLabel}
                icon={primaryIcon}
                responsiveLabel="2xl"
                floatingLabel={primaryLabel}
                floatingPosition="bottom"
                className="h-11"
              />
            </div>
          </div>

          {/* Mobile Trigger */}
          <div className="xl:hidden flex items-center gap-1">
            <LangDropdown />
            <ThemeToggleButton />
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? t("close_menu") : t("open_menu")}
              aria-expanded={menuOpen}
              className="flex items-center justify-center w-9 h-9 rounded-md text-foreground hover:text-primary transition-colors"
            >
              <HamburgerIcon open={menuOpen} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Full-Screen Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="fixed inset-0 z-40 xl:hidden bg-background flex flex-col pt-16"
          >
            <div className="flex-1 min-h-0 overflow-y-auto [&>*]:shrink-0 flex flex-col justify-start sm:justify-center gap-1 px-6 sm:px-10">
              {items.map((item, idx) => {
                  const cls =
                    "block w-full py-2.5 text-left text-2xl font-medium not-italic tracking-tight text-foreground transition-colors duration-200 hover:text-primary";
                  return (
                    <motion.div
                      key={item.key}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{
                        delay: idx * 0.05,
                        duration: 0.35,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      {item.type === "anchor" ? (
                        <button
                          onClick={() => goAnchor(item.page, item.id)}
                          className={cls}
                        >
                          {item.label}
                        </button>
                      ) : (
                        <Link
                          to={item.to}
                          onClick={() => setMenuOpen(false)}
                          className={cls}
                        >
                          {item.label}
                        </Link>
                      )}
                    </motion.div>
                  );
                })}

            </div>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.32, duration: 0.25 }}
              className="border-t border-border/10 px-6 sm:px-10 py-6 pb-safe-6 flex flex-col gap-3"
            >
              {showAccountCta && (
                <Button
                  size="lg"
                  design="outline"
                  to={ctaTo}
                  onClick={() => setMenuOpen(false)}
                  label={ctaLabel}
                  className="w-full"
                />
              )}
              <HoverCharButton
                size="lg"
                to={primaryTo}
                label={primaryLabel}
                charClassName="text-background dark:text-primary"
                className="w-full"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <div className="flex flex-col gap-1.25 w-4.5">
      <motion.span
        className="block h-[1.5px] bg-current rounded-full"
        animate={open ? { rotate: 45, y: 6.5 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.span
        className="block h-[1.5px] bg-current rounded-full"
        animate={open ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.15 }}
      />
      <motion.span
        className="block h-[1.5px] bg-current rounded-full"
        animate={open ? { rotate: -45, y: -6.5 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
  );
}
